/*
 * The product half of an article's address, printed immediately left of the Slug
 * box so the whole address reads as one line.
 *
 * An address is the pair (product slug, slug) - UQ_Article_ProductId_Slug
 * constrains the two together - and the author edits only the second half of it.
 * Without the prefix the box shows a free-standing name, which is what the field
 * used to be and is exactly the misreading that matters now: the same slug under
 * two products is two different addresses, and the thing that distinguishes them
 * is the part that was not on screen.
 *
 * IT WRAPS THE INPUT rather than sitting beside it, because the two have to
 * share a flex row and the row is the whole point; a component that returned
 * only the span would leave every call site to build that row itself, and the
 * second one would build it differently.
 *
 * THE ONE PLACE `/kb/` IS STILL SPELLED BY HAND, and deliberately so.
 * articleUrls.articleHref builds an ADDRESS; this is not one. It is the head of
 * an address whose tail is being typed, and there is no slug to hand the
 * builder: `articleHref(productSlug, '')` would emit the same characters by
 * accident, and the placeholder form would come back percent-encoded as
 * `/kb/%E2%80%A6/`. A label showing the SHAPE of a URL is presentation, and
 * belongs here rather than as a "partial" mode on a function whose whole value
 * is that every call it answers is a working link.
 *
 * PRESENTATIONAL, with three consequences worth stating because each is a rule
 * this component has to keep:
 *
 *   - a <span>, so there is nothing focusable in front of the input and nothing
 *     for a form to submit. A <label> would have been the other candidate and is
 *     wrong: a second label pointing at the input concatenates into its
 *     accessible name, so the field would announce itself as "Slug /kb/portal/".
 *   - aria-hidden, for the same reason from the other direction. The input's
 *     name stays "Slug", and the address is not lost to a screen reader: the
 *     hint under the field states it in a sentence, which is the form that reads
 *     as language rather than as punctuation.
 *   - it never gates the field. The error and hint are rendered by Field, below
 *     this row, and nothing here sits between them and the input.
 *
 * The row WRAPS rather than being a fixed two-column grid. A product slug is as
 * long as somebody made it, and in a rigid row a long one takes the width the
 * input needs - at which point the field the author is actually typing in is the
 * part that vanished. Wrapping puts the prefix on its own line at narrow widths
 * and gives the input the full width underneath; see .slug-field in the
 * stylesheet for the floor that triggers it.
 */

import type { ReactNode } from 'react';

export function SlugPrefix({
  productSlug,
  children,
}: {
  /**
   * Null while no product is chosen - the new-article page before the author
   * picks one - and while the catalogue that spells the slug is still loading.
   * Both render the same placeholder: the shape of the address is known, the
   * product is not.
   */
  productSlug: string | null;
  /** The Slug input itself. */
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="slug-field">
      <span className="slug-prefix" aria-hidden="true">
        /kb/{productSlug ?? '…'}/
      </span>
      {children}
    </div>
  );
}
